import type { FC } from 'react';
import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import { DropdownOption } from '@wix/da-ds/pkg/Dropdown/types';
import { changeOriginalStatus } from '../../../../actions/studio/originals';
import { OriginalStatusType } from '../../../../types/originals';

import s from './OriginalStatusEdit.scss';

interface Props {
  deviation: PapiDeviation;
  className?: string;
}

// Get initial status from deviation
const getStatusFromDeviation = (deviation: PapiDeviation): OriginalStatusType =>
  // custom_exclusives TODO: remove any when type is updated
  (deviation.originals as any)?.isOpen === false
    ? OriginalStatusType.CLOSED
    : OriginalStatusType.OPEN;

const OriginalStatusEdit: FC<Props> = ({ deviation, className }) => {
  const { t } = useTranslation();
  const inputId = useId();

  // Local state for optimistic UI - initialized from deviation
  const [status, setStatus] = useState<OriginalStatusType>(() =>
    getStatusFromDeviation(deviation)
  );

  const options: DropdownOption[] = [
    {
      label: t('studio.products.original.status.active'),
      value: OriginalStatusType.OPEN,
    },
    {
      label: t('studio.products.original.status.disabled'),
      value: OriginalStatusType.CLOSED,
    },
  ];

  const dispatchChangeOriginalStatus = useAction(changeOriginalStatus);
  const onSelect = useCallback(
    (newStatus: OriginalStatusType) => {
      // Optimistic update - change UI immediately
      setStatus(newStatus);
      // Dispatch action to trigger saga XHR
      dispatchChangeOriginalStatus({
        deviation,
        status: newStatus,
      });
    },
    [dispatchChangeOriginalStatus, deviation]
  );

  return (
    <SelectField
      id={inputId}
      className={cn(s['root'], className)}
      label="Status"
      items={options}
      value={status}
      onSelect={onSelect}
      isLabelHidden
      noBottomSpacing
      withBlankOption={false}
    />
  );
};
OriginalStatusEdit.displayName = 'OriginalStatusEdit';

export default OriginalStatusEdit;
